import {
  DEFAULT_LANG,
  ErrorCode,
  errorMessage,
  type ApiErrorBody,
  type Lang,
  type MessageKey,
} from '@asko/shared';

/**
 * Backend bilan ishlash uchun yagona nuqta.
 *
 * BT §7.4: foydalanuvchiga tushunarli xabar ko'rsatiladi. Shuning uchun
 * har qanday xato — tarmoq uzilishi bo'ladimi, 500 bo'ladimi — `ApiError`
 * ko'rinishida qaytadi.
 *
 * Har bir so'rovda `Accept-Language` yuboriladi — backend xabarlarni shu
 * tilda qaytaradi.
 */

const TOKEN_KEY = 'asko.accessToken';
const REFRESH_KEY = 'asko.refreshToken';

/** Joriy til. `I18nProvider` uni almashtirilganda yangilab turadi. */
let currentLang: Lang = DEFAULT_LANG;

export function setApiLang(lang: Lang): void {
  currentLang = lang;
}

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly status: number,
    readonly details?: Record<string, string[]>,
    readonly requestId?: string,
    /** Lug'at kaliti — til almashtirilganda xabar shu orqali tarjima qilinadi. */
    readonly messageKey?: MessageKey,
    readonly messageParams?: Record<string, string | number>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Maydon bo'yicha validatsiya xabari — forma ostida ko'rsatish uchun. */
  fieldError(field: string): string | undefined {
    return this.details?.[field]?.[0];
  }
}

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  set(accessToken: string, refreshToken: string) {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_KEY, refreshToken);
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Token muddati tugasa avtomatik yangilashga urinish (bir marta). */
  retryOnAuth?: boolean;
}

async function parseError(response: Response): Promise<ApiError> {
  let body: ApiErrorBody | undefined;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    // Javob JSON emas (masalan, proxy 502 qaytardi).
  }

  const code = body?.error?.code ?? ErrorCode.INTERNAL;
  return new ApiError(
    code,
    body?.error?.message ?? errorMessage(code, currentLang),
    response.status,
    body?.error?.details,
    body?.error?.requestId,
    body?.error?.messageKey,
    body?.error?.messageParams,
  );
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, retryOnAuth = true } = options;
  const token = tokenStore.get();

  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      headers: {
        'accept-language': currentLang,
        ...(body ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    // Tarmoq yo'q yoki server javob bermayapti.
    throw new ApiError(
      ErrorCode.INTERNAL,
      '',
      0,
      undefined,
      undefined,
      'e.networkDown',
    );
  }

  if (response.status === 401 && retryOnAuth && tokenStore.getRefresh()) {
    const refreshed = await tryRefresh();
    if (refreshed) return apiRequest<T>(path, { ...options, retryOnAuth: false });
  }

  if (!response.ok) throw await parseError(response);

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Access token muddati tugaganda uni jimgina yangilaydi. */
async function tryRefresh(): Promise<boolean> {
  const refreshToken = tokenStore.getRefresh();
  if (!refreshToken) return false;

  try {
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!response.ok) {
      tokenStore.clear();
      return false;
    }
    const data = (await response.json()) as { accessToken: string; refreshToken: string };
    tokenStore.set(data.accessToken, data.refreshToken);
    return true;
  } catch {
    tokenStore.clear();
    return false;
  }
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    apiRequest<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    apiRequest<T>(path, { method: 'POST', body, ...(signal ? { signal } : {}) }),
  patch: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    apiRequest<T>(path, { method: 'PATCH', body, ...(signal ? { signal } : {}) }),
};
